import { Injectable } from '@angular/core'
import { GtmCollectorService } from '@spartacus/tracking/tms/gtm'
import { CxEvent, PageType } from '@spartacus/core'
import { TmsCollectorConfig, WindowObject } from '@spartacus/tracking/tms/core'
import { ProductDetailsPageEvent, SearchPageResultsEvent } from '@spartacus/storefront'
import { CheckoutPageEvent } from '../events/checkout-page.event'
import { ProductClickEvent } from '../events/product-click.event'
import { MouseFlowEvent } from '../events/mouse-flow.event'
import { DataAnalyticsEventLabel, MbConstants } from '../../shared/mb-constants'
import { WishlistAddEntrySuccessEvent } from '../events/wishlist.events'
import { PoUploadFailed, PoUploadSuccess } from '../events/po-upload.events'
import { SearchPageZeroResultsEvent } from '../events/search-page-zero-results.event'
import { ProductFiltersEvent } from '../events/product-filters.event'
import {
  CartAddEntrySuccessEvent,
  CartEvent,
  CartPageEvent,
  CartRemoveEntrySuccessEvent
} from '@spartacus/cart/base/root'
import { OrderPlacedEvent } from '@spartacus/order/root'
import { MbCartEventCustomFields } from '../events/cart.events'

@Injectable({
  providedIn: 'root'
})
export class MbGtmCollectorService extends GtmCollectorService {

  mappers = [
    this.productDetailView,
    this.checkoutPageView,
    this.cartAddAction,
    this.cartDelAction,
    this.orderPageView,
    this.productClickAction,
    this.mouseFlowView,
    this.cartWishlistAction,
    this.poUploadClickAction,
    this.searchAction,
    this.searchZeroAction,
    this.filterAction
  ]

  pushEvent<T extends CxEvent> (config: TmsCollectorConfig,
                                windowObject: WindowObject,
                                event: T) {
    const commerceEvent = this.hasMapper(event)
    super.pushEvent(config, windowObject, commerceEvent || event)
  }

  protected hasMapper<T extends CxEvent> (event: T) {
    for (const mapper of this.mappers) {
      const m = mapper.bind(this)(event)
      if (m) {
        return m
      }
    }
    return undefined
  }

  protected productDetailView<T extends CxEvent> (event: T) {
    const v = (event as any)?.variants?.[0]
    return event instanceof ProductDetailsPageEvent && {
      event: 'productDetail',
      eventAction: 'productDetail',
      ecommerce: {
        detail: {
          products: [{
            name: v?.name?.replace(/<[^>]*>/g, ''),
            id: v?.code,
            price: v?.priceData?.value?.toString() || '0',
            currencyCode: v?.priceData?.currencyIso,
            list: event.navigation?.previousUrl?.includes(MbConstants.SEARCH_PAGE_ID)
              && MbConstants.SEARCH_PAGE_ID
              || event.navigation?.previousUrl?.split('?')?.[0]?.substring(event.navigation?.previousUrl?.lastIndexOf('/') + 1)?.replaceAll('-', ' ').replace('.html', '')
              || event.navigation?.context?.id?.substring(event.navigation?.context?.id?.lastIndexOf('/') + 1)?.replaceAll('-', ' ').replace('.html', ''),
            variant: v?.productLine?.productLine?.map((pl: any) => pl.value).join(' ').replace(/<[^>]*>/g, ''),
            category: event.categories?.[0].name?.replace(/<[^>]*>/g, ''),
            dimension01: v?.hasPriceOnRequest && 'Pre-Order' || 'In Stock',
            isProduct: true
          }]
        }
      }
    }
  }

  protected checkoutPageView<T extends CxEvent> (event: T) {
    return (event instanceof CartPageEvent || event instanceof CheckoutPageEvent) && {
      event: 'checkout',
      ecommerce: {
        currencyCode: event.cart?.totalPrice?.currencyIso,
        checkout: {
          actionField: {
            step: event.step,
            option: event.cart?.paymentDetails?.paymentMode?.code
          },
          products: event.cart?.entries?.map(e => ({
            id: e.product?.code,
            name: e.product?.name?.replace(/<[^>]*>/g, ''),
            price: e.basePrice?.value?.toString() || '0',
            category: e.product?.categoryNamePath,
            list: e.product?.categoryNamePath?.split('/'),
            quantity: e.quantity,
            coupon: e.promotions?.map(p => p.promotion?.code).join(' '),
            dimension01: e.purchasable && 'In Stock' || 'Pre-Order',
            metric01: e.basePrice?.value?.toString() || '0',
            variant: e.product?.productLine?.entries?.map(pl => pl.value).join(' ').replace(/<[^>]*>/g, ''),
          })),
        }
      }
    }
  }

  protected filterAction<T extends CxEvent> (event: T) {
    return (event instanceof ProductFiltersEvent) && {
      event: 'click',
      eventCategory: 'Variant Selectors',
      eventAction: event.eventAction,
      eventLabel: event.eventLabel
    }
  }

  protected searchAction<T extends CxEvent> (event: T) {
    return (event instanceof SearchPageResultsEvent) && {
      event: 'siteSearch',
      searchTerm: event.searchTerm,
    }
  }

  protected searchZeroAction<T extends CxEvent> (event: T) {
    return (event instanceof SearchPageZeroResultsEvent) && {
      event: 'search',
      eventCategory: 'Search Results amount',
      eventAction: 0,
      eventLabel: event.searchTerm
    }
  }

  protected poUploadClickAction<T extends CxEvent> (event: T) {
    return (event instanceof PoUploadSuccess || event instanceof PoUploadFailed) && {
      event: 'click',
      eventCategory: 'Ecommerce',
      eventAction: 'PO Upload',
      eventLabel: event.poUploadStatus
    }
  }

  protected cartWishlistAction<T extends CxEvent> (event: T) {
    return event instanceof WishlistAddEntrySuccessEvent && {
      event: 'addToCart',
      eventCategory: 'Ecommerce',
      eventAction: 'Add to cart',
      eventLabel: 'wishlist',
      ecommerce: {
        add: {
          products: [{
            id: event.entry?.product?.code,
            name: event.entry?.product?.name?.replace(/<[^>]*>/g, ''),
            price: event.entry?.basePrice?.value?.toString() || '0',
            currencyCode: event.entry?.basePrice?.currencyIso,
            category: event.entry?.product?.lastCategoryName,
            quantity: event.quantityAdded,
            list: this.getListValue(event),
            dimension01: event.entry?.purchasable && 'In Stock' || 'Pre-Order',
            metric01: event.entry?.basePrice?.value?.toString() || '0',
            isProduct: true,
            variant: event.entry?.product?.productLine?.entries?.map(pl => pl.value).join(' ').replace(/<[^>]*>/g, ''),
          }]
        }
      }
    }
  }

  protected cartAddAction<T extends CxEvent> (event: T) {
    if (event instanceof CartAddEntrySuccessEvent) {
      // custom fields are added in MbCartEventBuilder
      const { url, addToCartClickSource, addToCartEventLabel } = event as unknown as CartEvent & MbCartEventCustomFields
      return {
        event: 'addToCart',
        eventCategory: 'Ecommerce',
        eventAction: 'Add to cart',
        eventLabel:
          (addToCartEventLabel !== DataAnalyticsEventLabel.UNKNOWN && addToCartEventLabel)
          || (url?.includes(MbConstants.CART_PAGE_URL) && DataAnalyticsEventLabel.QUICK_ADD)
          || (url?.includes(MbConstants.MY_ACCOUNT_ORDER_PAGE) && DataAnalyticsEventLabel.ORDER_HISTORY)
          || (event.entry?.purchasable && DataAnalyticsEventLabel.ADD_TO_CART)
          || DataAnalyticsEventLabel.REQUEST_PRICE,
        item_list_name: addToCartClickSource,
        ecommerce: {
          add: {
            products: [{
              id: event.entry?.product?.code,
              name: event.entry?.product?.name?.replace(/<[^>]*>/g, ''),
              price: event.entry?.basePrice?.value?.toString() || '0',
              currencyCode: event.entry?.basePrice?.currencyIso,
              category: event.entry?.product?.lastCategoryName,
              quantity: event.quantityAdded,
              list: this.getListValue(event),
              dimension01: event.entry?.purchasable && 'In Stock' || 'Pre-Order',
              metric01: event.entry?.basePrice?.value?.toString() || '0',
              isProduct: true,
              variant: event.entry?.product?.productLine?.entries?.map(pl => pl.value).join(' ').replace(/<[^>]*>/g, ''),
            }]
          }
        }
      }
    }
    return undefined
  }

  protected cartDelAction<T extends CxEvent> (event: T) {
    return event instanceof CartRemoveEntrySuccessEvent && {
      event: 'removeFromCart',
      ecommerce: {
        remove: {
          products: [{
            id: event.entry?.product?.code,
            name: event.entry?.product?.name?.replace(/<[^>]*>/g, ''),
            price: event.entry?.basePrice?.value?.toString() || '0',
            currencyCode: event.entry?.basePrice?.currencyIso,
            category: event.entry?.product?.categoryNamePath,
            list: event.entry?.product?.categoryNamePath?.split('/'),
            quantity: event.entry.quantity,
            dimension01: event.entry?.purchasable && 'In Stock' || 'Pre-Order',
            metric01: event.entry?.basePrice?.value?.toString() || '0',
            variant: event.entry?.product?.productLine?.entries?.map(pl => pl.value).join(' ').replace(/<[^>]*>/g, ''),
          }]
        }
      }
    }
  }

  protected orderPageView<T extends CxEvent> (event: T) {
    return (event instanceof OrderPlacedEvent) && {
      event: 'purchase',
      ecommerce: {
        currencyCode: event.order.totalPrice?.currencyIso,
        purchase: {
          actionField: {
            id: event.order.code,   // Bestell- oder Transaktionsnummer
            affiliation: event.order.paymentDetails?.paymentMode?.name, //payment type
            revenue: event.order.erpTotalPrice?.value?.toString() || '0',  // inkl. Versand
            shipping: event.order.erpFright?.value?.toString() || '0',
            tax: event.order.erpTaxValue?.value
          },
          products: event.order.entries?.map(e => ({
            id: e.product?.code,
            name: e.product?.name?.replace(/<[^>]*>/g, ''),
            price: e.basePrice?.value?.toString() || '0',
            category: e.product?.categoryNamePath,
            list: e.product?.categoryNamePath?.split('/'),
            variant: e.product?.productLine?.entries?.map(pl => pl.value).join(' ').replace(/<[^>]*>/g, ''),
            quantity: e.quantity,
            coupon: e.promotions?.map(p => p.promotion?.code).join(' '),
            dimension01: e.purchasable && 'In Stock' || 'Pre-Order',
            metric01: e.erpPrice?.value?.toString() || '0'  // Original Price
          }))
        }
      }
    }
  }

  protected productClickAction<T extends CxEvent> (event: T) {
    if (event instanceof ProductClickEvent) {
      const { product, navigation } = (event as ProductClickEvent)
      const price = product?.price || (product as any)?.priceData || {}
      const clickSource = (event.event?.target as HTMLLinkElement)?.dataset?.analyticsSrc
      return {
        event: 'productClick',
        eventCategory: 'Ecommerce',
        eventAction: 'Product Click',
        item_list_name: clickSource,
        ecommerce: {
          click: {
            actionField: {
              list: navigation?.context?.id?.substring(navigation?.context?.id?.lastIndexOf('/')+1)?.replaceAll('-', ' ').replace('.html', ''),
            },
            products: [{
              id: product?.code,
              name: product?.name?.replace(/<[^>]*>/g, ''),
              price: product?.code?.includes('-') ? (price.value?.toString() || '0') : '0',
              currencyCode: price.currencyIso,
              category: product?.lastCategoryName,
              variant: product?.searchInformationMap?.entry?.map(pl => pl.value).join(' ').replace(/<[^>]*>/g, ''),
              list: navigation?.context?.id?.substring(navigation?.context?.id?.lastIndexOf('/')+1)?.replaceAll('-', ' ').replace('.html', ''),
              // position: productObj.position, // not able to get it in current implementation without hack a lot
            }]
          }
        },
      }
    }
    return undefined
  }

  protected mouseFlowView<T extends CxEvent> (event: T) {
    return event instanceof MouseFlowEvent && {
      event: 'mfsID',
      dimension3: event.mouseFlow?.getSessionId()
    }
  }

  protected getListValue<T extends CartEvent> (event: T) {
    const { url, page } = event
    let list = url?.split('?')?.[0]?.substring(url.lastIndexOf('/') + 1)?.replaceAll('-', ' ').replace('.html', '')
    if (page?.type !== PageType.PRODUCT_PAGE) {
      if (url?.includes(MbConstants.SEARCH_PAGE_ID)) {
        list = MbConstants.SEARCH_PAGE_ID
      }
    } else {
      list = list?.split('#')?.[0]
    }
    return list
  }
}
